export interface SpeakOptions {
  onBoundary?: (charIndex: number) => void;
  onEnd?: () => void;
  onStart?: () => void;
  queue?: boolean;
}

let activeRun: { owner: symbol; finishers: Set<() => void> } | null = null;

export function createSpeechSession() {
  const owner = Symbol("speech session");

  const cancel = () => {
    if (activeRun?.owner !== owner) {
      return;
    }
    activeRun = null;
    window.speechSynthesis?.cancel();
  };

  const speak = (text: string, rate: number, options?: SpeakOptions) => {
    const synth = window.speechSynthesis;
    if (!synth) {
      return;
    }

    if (options?.queue) {
      if (activeRun?.owner !== owner) {
        return;
      }
    } else {
      const previous = activeRun;
      activeRun = { finishers: new Set(), owner };
      if (previous && previous.owner !== owner) {
        for (const finish of previous.finishers) {
          finish();
        }
      }
      synth.cancel();
    }

    const run = activeRun;
    const isCurrent = () => activeRun === run;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = rate;

    let finished = false;
    const finish = () => {
      if (finished) {
        return;
      }
      finished = true;
      run.finishers.delete(finish);
      options?.onEnd?.();
    };
    run.finishers.add(finish);

    utterance.onstart = () => {
      if (isCurrent()) {
        options?.onStart?.();
      }
    };
    utterance.onboundary = (event) => {
      if (isCurrent()) {
        options?.onBoundary?.(event.charIndex);
      }
    };
    const onFinish = () => {
      if (isCurrent()) {
        finish();
      }
    };
    utterance.onend = onFinish;
    utterance.onerror = onFinish;

    synth.speak(utterance);
  };

  return { cancel, speak };
}
